import { FC } from 'react'
import { Menu, Text, TextColour } from '@reapit/elements'
import { SubMenuNavigation } from '../types'
import { isMenuActive } from '../utils'
import { isEmpty } from '@reapit-sales/utilities'

/**
 * `SubMenu` renders either a single menu item or a group of submenu items
 * based on the `group` prop provided. It is typically used in navigation bars.
 *
 * @component
 * @param {SubMenuNavigation} props - Props for configuring the submenu.
 * @param {SubMenuNavigation[]} [props.group] - Optional group of submenu items to be rendered.
 * @returns {JSX.Element} A menu item or group of menu items based on access permissions.
 */
export const SubMenu: FC<SubMenuNavigation> = ({ group, displayAsGroup, ...restProps }) => {
  return (
    <>
      {group && displayAsGroup !== false && (
        <Menu.Group>
          {group
            ?.filter((subMenuItem) => subMenuItem.hasAccess !== false)
            .map((groupSubmenuItem) => <MenuItem {...groupSubmenuItem} key={groupSubmenuItem.label} />)}
        </Menu.Group>
      )}
      {!group && <MenuItem {...restProps} />}
    </>
  )
}

/**
 * `MenuItem` is a single clickable item in a navigation menu.
 *
 * - If `href` is provided, the item is rendered as a link (`Menu.AnchorItem`)
 * with active state handling.
 * - If not, it is rendered as a clickable `Menu.Item` with an `onClick` handler.
 *
 * @component
 * @param {SubMenuNavigation} props - Props for an individual menu item.
 * @param {string} props.label - Text label for the menu item.
 * @param {ReactNode} [props.leftIcon] - Icon to render on the left.
 * @param {ReactNode} [props.rightIcon] - Icon to render on the right.
 * @param {() => void} [props.onClick] - Function to call on click.
 * @param {string} [props.href] - Key used to determine active state.
 * @returns {JSX.Element} Rendered menu item with optional icons and click handling.
 */
const MenuItem: FC<SubMenuNavigation> = ({ label, leftIcon, href, rightIcon, badge, color, ...restProps }) => {
  // If the `href` is provided, render as an anchor item; otherwise, use a regular menu item.
  if (!isEmpty(href)) {
    return (
      <Menu.AnchorItem
        {...restProps}
        href={href ?? ''}
        aria-current={isMenuActive(href)}
        iconLeft={leftIcon}
        iconRight={rightIcon}
        badge={badge}
      >
        {color ? (
          <Text as="span" colour={color as TextColour} size="base" weight="regular">
            {label}
          </Text>
        ) : (
          label
        )}
      </Menu.AnchorItem>
    )
  }
  return (
    <Menu.Item
      onClick={restProps.onClick}
      aria-current={isMenuActive(href)}
      iconLeft={leftIcon}
      iconRight={rightIcon}
      badge={badge}
    >
      {color ? (
        <Text as="span" colour={color as TextColour} size="base" weight="regular">
          {label}
        </Text>
      ) : (
        label
      )}
    </Menu.Item>
  )
}
